import { useCallback, useEffect, useRef } from 'react';
import { API_BASE_URL } from '@/api/client';
import { useVisitorStore } from '@/stores/visitorStore';

export type AnalyticsEventType =
  | 'page_view'
  | 'click'
  | 'scroll_depth'
  | 'map_opened'
  | 'call_clicked'
  | 'whatsapp_clicked'
  | 'share_clicked'
  | 'product_viewed'
  | 'feedback_submitted'
  | 'lead_submitted';

export interface AnalyticsPayload {
  event_type: AnalyticsEventType;
  business_id?: string;
  session_id?: string;
  visitor_fingerprint?: string;
  metadata?: Record<string, unknown>;
  page_path?: string;
}

export function useAnalytics(businessId?: string) {
  const visitorFingerprint = useVisitorStore((s) => s.visitorFingerprint);
  const sessionId = useVisitorStore((s) => s.sessionId);
  const pageViewSent = useRef(false);

  const track = useCallback(
    (eventType: AnalyticsEventType, metadata: Record<string, unknown> = {}) => {
      const payload: AnalyticsPayload = {
        event_type: eventType,
        business_id: businessId,
        session_id: sessionId || undefined,
        visitor_fingerprint: visitorFingerprint || undefined,
        metadata,
        page_path: window.location.pathname,
      };

      const url = `${API_BASE_URL.replace(/\/$/, '')}/public/analytics/events`;
      const body = JSON.stringify(payload);

      if (navigator.sendBeacon) {
        const blob = new Blob([body], { type: 'application/json' });
        navigator.sendBeacon(url, blob);
      } else {
        fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body,
          keepalive: true,
        }).catch(() => undefined);
      }
    },
    [businessId, sessionId, visitorFingerprint],
  );

  useEffect(() => {
    if (pageViewSent.current) return;
    pageViewSent.current = true;
    track('page_view');
  }, [track]);

  return { track };
}
